import { IconStethoscope } from '@tabler/icons-react';
import {
  BenefitType,
  MedicalPlanForEnrollment,
  SelfEnrollType,
  SelfReportType,
} from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import {
  MonolithMethodNames,
  UseMonolithQueryProps,
  exportEmployeePlanList,
  getApplicantsFromWhoIsEnrolled,
  getFamilyUnitFromWhoIsEnrolled,
  getWhoIsEnrolled,
  useFlowId,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect } from 'react';

import { useAnalytics } from '../../Analytics';
import { useEnrollmentAllowanceStore } from '../../EnrollmentAllowanceCard';
import { FullPageLoader } from '../../FullPageLoader';
import {
  PlanElectionForm,
  formatExtendedPlanDetails,
  mapManualElectionFormToAddToCartPlan,
  mapPlanElectionItemToEnrollmentAllowanceStorePlan,
  usePlanElectionForm,
} from '../../PlanElectionForm';
import {
  ManualSubmissionStep,
  ManualSubmissionStepSubmit,
  ManualSubmissionStepType,
} from '../ManualSubmissionStep';
import {
  getDefaultMedicalPlanFormFields,
  mapMedicalPlanToPlanElectionItem,
  validateMedicalPlanEnrollmentSelectedEnrolled,
} from './ManualSubmissionMedicalPlanStepUtils';

const searchMedicalPlansMethod: MonolithMethodNames =
  'majorMedicalControllerSearchMedicalPlansForOnboardingPeriod';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  onSubmit: () => void;
  isDon?: boolean;
};

export const ManualSubmissionMedicalPlanStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  onSubmit: onSubmitProp,
  isDon = false,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const flowId = useFlowId();
  const setMedicalPlans = useEnrollmentAllowanceStore(
    (state) => state.setMedicalPlans
  );
  const setFamilyUnit = useEnrollmentAllowanceStore(
    (state) => state.setFamilyUnit
  );
  const setNumberOfDependents = useEnrollmentAllowanceStore(
    (state) => state.setNumberOfDependents
  );

  const { mutate: mutateAddToCart } = useMonolithMutation({
    method: 'benefitsControllerAddBenefitPlanToCart',
    shouldReloadPage: false,
  });

  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const majorMedicalPlan = benefits?.majorMedicalBenefit?.plan;
  const benefitInsuredPeople = benefits?.majorMedicalBenefit?.insuredPeople;
  const isCombinedPlanStructure =
    isDefined(majorMedicalPlan?.isCombinedPlan) &&
    majorMedicalPlan?.isCombinedPlan;

  const handleGetDefaultFormFields = (allWhoIsEnrolled: string[] = []) => [
    getDefaultMedicalPlanFormFields(
      benefitInsuredPeople
        ? getWhoIsEnrolled(benefitInsuredPeople)
        : allWhoIsEnrolled,
      majorMedicalPlan?.externalID,
      majorMedicalPlan?.name,
      majorMedicalPlan?.carrierName,
      majorMedicalPlan?.premium,
      majorMedicalPlan?.selfEnroll,
      majorMedicalPlan?.isCombinedPlan,
      majorMedicalPlan?.policyType,
      majorMedicalPlan?.extendedPlanDetails
    ),
  ];

  const {
    isSubmitting,
    planElectionForm,
    allWhoIsEnrolledOptions,
    whoIsEnrolledInMultiplePlans,
    insured,
    onboardingPeriod,
    insuredIdToNameMap,
    selectedEnrolled,
    onSubmit,
  } = usePlanElectionForm({
    employeeId,
    onboardingPeriodId,
    getDefaultFormFields: handleGetDefaultFormFields,
    onPlansChange: (plans) => {
      const storePlans =
        mapPlanElectionItemToEnrollmentAllowanceStorePlan(plans);
      setMedicalPlans(storePlans);
    },
    onSubmit: async ({ data }) => {
      const { plans } = data;
      const premium = plans.reduce((prev, plan) => prev + plan.premium, 0);
      const computedExtendedPlanDetails = formatExtendedPlanDetails(
        plans,
        insuredIdToNameMap
      );

      const isValidEnrolled = validateMedicalPlanEnrollmentSelectedEnrolled(
        benefits,
        selectedEnrolled,
        insuredIdToNameMap
      );

      if (!isValidEnrolled) {
        await sendEvent('plan_election_validation_error', {
          onboarding_period_id: onboardingPeriodId,
          reason: 'covered_on_supplemental_plan',
        });
        return false;
      }

      const {
        isCombinedPlan,
        planIds,
        extendedPlanDetails,
        benefitsSummaryUrl,
        maxOutOfPocket,
        isHsaEligible,
        planName,
        carrierName,
        deductible,
        planMarket,
        isSelfEnroll,
        policyType,
      } = mapManualElectionFormToAddToCartPlan(
        plans,
        isCombinedPlanStructure,
        computedExtendedPlanDetails
      );

      let navigate = false;
      try {
        await mutateAddToCart(
          [
            onboardingPeriodId,
            {
              plans: [
                {
                  type: BenefitType.MAJOR_MEDICAL,
                  plan: {
                    selfReportType: SelfReportType.NOT_APPLICABLE,
                    isCombinedPlan,
                    externalID: planIds,
                    extendedPlanDetails,
                    benefitsSummaryUrl,
                    maxOutOfPocket,
                    isHsaEligible,
                    name: planName,
                    carrierName,
                    deductible,
                    premium,
                    planMarket,
                    selfEnroll: isSelfEnroll
                      ? SelfEnrollType.SELF_ENROLL
                      : SelfEnrollType.NOT_SELF_ENROLL,
                    policyType,
                  },
                  insuredIds: Array.from(selectedEnrolled),
                },
              ],
            },
          ],
          { flowId }
        );

        navigate = true;
      } catch {
        /* empty */
      }

      return navigate;
    },
  });

  const familyUnitInfo = getFamilyUnitFromWhoIsEnrolled(
    insured,
    selectedEnrolled
  );

  useEffect(() => {
    setFamilyUnit(familyUnitInfo.familyUnit);
    setNumberOfDependents(familyUnitInfo.numberOfDependents);
  }, [familyUnitInfo, setFamilyUnit, setNumberOfDependents]);

  if (!insured) {
    return <FullPageLoader />;
  }

  const handleStepSubmit: ManualSubmissionStepSubmit = async () => {
    if (await planElectionForm.trigger()) {
      onSubmitProp();
      return { navigate: await onSubmit() };
    }

    return { navigate: false };
  };

  const handleGetParams = (
    whoIsEnrolled: string[]
  ): UseMonolithQueryProps<typeof searchMedicalPlansMethod>['params'] => {
    if (!onboardingPeriod || !employee || !insured) {
      return undefined;
    }

    const applicants = getApplicantsFromWhoIsEnrolled(
      insured,
      whoIsEnrolled,
      onboardingPeriod.effectiveFrom,
      employee.email
    );

    return [
      onboardingPeriod.id,
      {
        insuredIds: whoIsEnrolled,
        applicants,
      },
    ];
  };

  const handleDownloadPlans = (medicalPlans?: MedicalPlanForEnrollment[]) => {
    if (medicalPlans) {
      exportEmployeePlanList(
        medicalPlans,
        `${insured.employee.firstName} ${insured.employee.lastName}`
      );
    }
  };

  return (
    <ManualSubmissionStep
      step={ManualSubmissionStepType.MEDICAL_PLAN}
      submitLabel="Continue"
      onboardingPeriodId={onboardingPeriodId}
      onSubmit={handleStepSubmit}
      isSubmitDisabled={isSubmitting}
      employerId={employerId}
      employeeId={employeeId}
      isDon={isDon}
      withReviewCard
    >
      <PlanElectionForm
        getDefaultPlanElectionFormFields={() => handleGetDefaultFormFields()[0]}
        mapDataToPlanElectionItem={mapMedicalPlanToPlanElectionItem}
        whoIsEnrolledInMultiplePlans={whoIsEnrolledInMultiplePlans}
        isCombinedPlanStructure={isCombinedPlanStructure}
        whoIsEnrolledOptions={allWhoIsEnrolledOptions}
        addPlanButtonText="Add another medical plan"
        insuredIdToNameMap={insuredIdToNameMap}
        benefitType={BenefitType.MAJOR_MEDICAL}
        onDownloadPlans={handleDownloadPlans}
        planElectionForm={planElectionForm}
        method={searchMedicalPlansMethod}
        getParams={handleGetParams}
        icon={IconStethoscope}
        title="Medical plan"
      />
    </ManualSubmissionStep>
  );
};
